/**
 * Canonical registry of map spot status colors.
 *
 * Map spots (the desk/seat circles on /map) are rendered by two pipelines
 * that share this palette through CSS custom properties:
 * - the SVG map pipeline, styled by class selectors in
 *   `apps/web/src/styles/map.css` and `packages/map/src/App.css`
 * - the map-builder pipeline, which resolves the same variables at runtime
 *   in `getInteractiveZoneStyles()` (packages/map-builder)
 *
 * Every spot color must be declared here so themes (static per-org themes
 * today, organization settings in the future) can override any of them by
 * setting the corresponding `themeKey` in a theme's `map` block.
 */
export type MapSpotColorDefinition = {
  /** CSS custom property both map pipelines read at render time */
  cssVar: string;
  /** Key inside a theme object's `map` block that overrides this color */
  themeKey: string;
  /** Value used when neither the theme nor its base defines the color */
  defaultValue: string;
};

export const MAP_SPOT_COLORS = {
  availableColor: {
    cssVar: '--map-available-color',
    themeKey: 'availableColor',
    defaultValue: 'rgba(156, 215, 168, 1)'
  },
  notAvailableColor: {
    cssVar: '--map-not-available-color',
    themeKey: 'notAvailableColor',
    defaultValue: 'rgba(169, 169, 169, 1)'
  },
  spotNotBookeableUnavailableStroke: {
    cssVar: '--map-spot-not-bookeable-unavailable-stroke',
    themeKey: 'spotNotBookeableUnavailableStroke',
    defaultValue: 'rgba(153, 95, 95, 0.46)'
  },
  spotNotBookeableUnavailableFill: {
    cssVar: '--map-spot-not-bookeable-unavailable-fill',
    themeKey: 'spotNotBookeableUnavailableFill',
    defaultValue: 'rgba(232, 236, 239, 1)'
  },
  spotNotBookeableStroke: {
    cssVar: '--map-spot-not-bookeable-stroke',
    themeKey: 'spotNotBookeableStroke',
    defaultValue: 'rgba(255, 255, 255, 1)'
  },
  spotNotBookeableFill: {
    cssVar: '--map-spot-not-bookeable-fill',
    themeKey: 'spotNotBookeableFill',
    defaultValue: '#223c5b'
  },
  spotRequiresApprovalStroke: {
    cssVar: '--map-spot-requires-approval-stroke',
    themeKey: 'spotRequiresApprovalStroke',
    defaultValue: 'rgba(255, 255, 255, 1)'
  },
  spotRequiresApprovalFill: {
    cssVar: '--map-spot-requires-approval-fill',
    themeKey: 'spotRequiresApprovalFill',
    defaultValue: 'rgba(252, 211, 77, 1)'
  },
  spotBookeableWithSchedulesStroke: {
    cssVar: '--map-spot-bookeable-with-schedules-stroke',
    themeKey: 'spotBookeableWithSchedulesStroke',
    defaultValue: 'rgba(255, 255, 255, 1)'
  },
  spotBookeableWithSchedulesFill: {
    cssVar: '--map-spot-bookeable-with-schedules-fill',
    themeKey: 'spotBookeableWithSchedulesFill',
    defaultValue: 'var(--map-available-color)'
  },
  spotFullyAvailableStroke: {
    cssVar: '--map-spot-fully-available-stroke',
    themeKey: 'spotFullyAvailableStroke',
    defaultValue: 'rgba(255, 255, 255, 1)'
  },
  spotFullyAvailableFill: {
    cssVar: '--map-spot-fully-available-fill',
    themeKey: 'spotFullyAvailableFill',
    defaultValue: 'var(--map-available-color)'
  },
  spotNotAssignedFill: {
    cssVar: '--map-spot-not-assigned-fill',
    themeKey: 'spotNotAssignedFill',
    defaultValue: 'rgba(232, 236, 239, 1)'
  },
  spotNotAssignedStroke: {
    cssVar: '--map-spot-not-assigned-stroke',
    themeKey: 'spotNotAssignedStroke',
    defaultValue: 'var(--map-available-color)'
  },
  spotSelectedStroke: {
    cssVar: '--map-spot-selected-stroke',
    themeKey: 'spotSelectedStroke',
    defaultValue: 'var(--color-primary)'
  },
  /** Ring around the avatar when the reservation is checked in / live */
  spotCheckedInStroke: {
    cssVar: '--map-spot-checked-in-stroke',
    themeKey: 'spotCheckedInStroke',
    defaultValue: '#34d399'
  },
  /** Avatar circle background when the user has no photo or initials */
  spotAvatarFallbackFill: {
    cssVar: '--map-spot-avatar-fallback-fill',
    themeKey: 'spotAvatarFallbackFill',
    defaultValue: '#223c5b'
  },
  /** Walk-up resources (is_walkup) — reserved for the walk-up spot style */
  spotWalkUpFill: {
    cssVar: '--map-spot-walk-up-fill',
    themeKey: 'spotWalkUpFill',
    defaultValue: 'rgba(156, 215, 168, 0.8)'
  },
  spotWalkUpStroke: {
    cssVar: '--map-spot-walk-up-stroke',
    themeKey: 'spotWalkUpStroke',
    defaultValue: 'var(--map-available-color)'
  }
} as const satisfies Record<string, MapSpotColorDefinition>;

export type MapSpotColorName = keyof typeof MAP_SPOT_COLORS;

type ThemeMapBlock = Record<string, string | undefined> | undefined;

/**
 * Resolves every spot color to its CSS variable assignment, preferring the
 * theme's `map` block, then the base theme's, then the registry default.
 * Spread the result into a theme's CSS variable map (see `mapTheme`).
 */
export const mapSpotCssVariables = (
  themeMap: ThemeMapBlock,
  baseMap?: ThemeMapBlock
): Record<string, string> => {
  const cssVariables: Record<string, string> = {};
  for (const { cssVar, themeKey, defaultValue } of Object.values(
    MAP_SPOT_COLORS
  )) {
    cssVariables[cssVar] =
      themeMap?.[themeKey] ?? baseMap?.[themeKey] ?? defaultValue;
  }
  return cssVariables;
};
